import { yupResolver } from "@hookform/resolvers/yup";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { VerificationProcessService } from "../../../Component/Api/VerificationAgency/VerificationProcess/VerificationProcessService";
import { ConfirmationDialog } from "../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { MDButton } from "../../../Component/Common/Components/MDButton";
import { ColumnSortOption } from "../../../Component/Model/Dto/ColumnSortOption";
import { VerificationProcess } from "../../../Component/Model/Dto/VerificationAgency/VerificationProcessTracker";
import { EntriesPerPage } from "../../../Component/Model/Table/Table";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { SupportingDocuments } from "../../Common/SupportingDocuments/SupportingDocuments";
import { VAProcessTrackerTableMapper } from "../../VerificationAgency/BEEScorecards/VAProcessTrackerTable";
import { VerificationProcessTrackerEdit } from "../../VerificationAgency/BEEScorecards/VerificationStatusTrackerEditModal";
import { VerificationStatusTrackerScheme } from "../../VerificationAgency/BEEScorecards/VerificationStatusTrackerValidation";
import { ScorecardVerificationProcessTrackerEdit } from "../ScorecardVerificationProcessTrackerEdit";
import { ScorecardVerificationModal } from "./ScorecardVerificationModal/ScorecardVerificationModal";

interface IProps {
    scorecardId: string
    showScorecardVerificationProcessTrackerModal: boolean;
    closeScorecardVerificationProcessTrackerModal: () => void;
}

export const ScorecardVerificationProcessTracker = (props: IProps) => {

    const [verificationProcesssTrackerData, setVerificationProcesssTrackerData] = useState<VerificationProcess.VerificationProcessSearchResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>('');
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [showStatusTracker, setShowStatusTracker] = useState<boolean>(false);
    const [showEmailProgress, setShowEmailProgress] = useState<boolean>(false);
    const [showAllEmailProgress, setShowAllEmailProgress] = useState<boolean>(false);

    const [selectedScorecardId, setSelectedScorecardId] = useState<string>("");
    const [selectedId, setSelectedId] = useState<number>(0);
    const [documentsPopUp, setDocumentsPopUp] = useState<boolean>(false);
    const link = 'VerificationProcessRow';
    const [selectedDocumentId, setSelectedDocumentId] = useState<number>(0);
    const [verificationProcessId, setVerificationProcessId] = useState<number>(0);

    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const confirmDeleteMessage = "Are you sure you want to delete this verification process entry?";
    const [selectedProcessId, setSelectedProcessId] = useState<number>(0);
    const [refreshFlag, setRefreshFlag] = useState(false);


    const verificationProcessTrackerSearchRequest: VerificationProcess.VerificationProcessSearchRequest = {
        scorecardId: props.scorecardId,
        columnSortOptions: columnSortOptions,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue as number,
        searchValue: searchValue
    };

    const handleRefresh = () => {
        setRefreshFlag(!refreshFlag);
    };

    const { control, handleSubmit } = useForm({
        mode: 'all',
        resolver: yupResolver(VerificationStatusTrackerScheme)
    });

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let response = await VerificationProcessService.Search(verificationProcessTrackerSearchRequest);
            setVerificationProcesssTrackerData(response.data.results);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setTotalItemsCount(response.data.totalItemsCount);
        } catch (error) { }
        finally {
            setIsLoading(false);
        }
    }

    const editVerificationProcess = (id: number) => {
        setShowStatusTracker(true);
        setSelectedId(id);
        setSelectedScorecardId(props.scorecardId);
    }

    const uploadSupportingDocuments = (id: number) => {
        setSelectedDocumentId(id);
        setDocumentsPopUp(true);
    }

    const sendUsersEmail = (recordId: number) => {
        setShowEmailProgress(true);
        setVerificationProcessId(recordId);
    }

    const handleSendAllUsersEmail = () => {
        setShowAllEmailProgress(true);
    }
    const handleCloseAllEmailProgress = () => {
        setShowAllEmailProgress(false);
    }

    const handleCloseEmailProgress = () => {
        setShowEmailProgress(false);
        setVerificationProcessId(0);
    }

    const deleteVerificationProcess = (id: number) => {
        setShowConfirmationDialog(true);
        setSelectedProcessId(id);
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedProcessId(0);
    }

    const handleDelete = async () => {
        try {
            await VerificationProcessService.DeleteVerificationProcess(selectedProcessId)
            toast.success("Successfully deleted verification process");
            fetchData();
        } catch (error) {
            console.log(error)
        } finally {
            handleCloseConfirmation();
        }
    }

    const onDocumentPopupClose = () => {
        setDocumentsPopUp(false);
        fetchData();
    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, searchValue, pageNumber, columnSortOptions, refreshFlag]);

    const verificationProcesssTrackerTableData = VAProcessTrackerTableMapper(verificationProcesssTrackerData, editVerificationProcess, uploadSupportingDocuments, sendUsersEmail, deleteVerificationProcess);

    function handleCloseStatusTracker(): void {
        setShowStatusTracker(false);
        fetchData();
    }

    return (
        <>
            <Dialog open={props.showScorecardVerificationProcessTrackerModal} onClose={props.closeScorecardVerificationProcessTrackerModal} aria-labelledby="form-dialog-title" maxWidth="xl" fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={props.closeScorecardVerificationProcessTrackerModal}>
                        Verification Process
                    </CustomDialogTitle>
                <DialogContent>
                    <VerificationProcessTrackerEdit scorecardId={props.scorecardId} showScorecardVerificationProcessTrackerModal={props.showScorecardVerificationProcessTrackerModal} closeScorecardVerificationProcessTrackerModal={props.closeScorecardVerificationProcessTrackerModal} onRefresh={handleRefresh} />
                    <DataTable
                        table={verificationProcesssTrackerTableData}
                        entriesPerPage={entriesPerPage}
                        pageNumber={pageNumber}
                        filteredItemsCount={filteredItemsCount}
                        totalItemsCount={totalItemsCount}
                        entriesPerPageChanged={entriesPerPageChanged}
                        pageNumberChanged={pageNumberChanged}
                        isLoading={isLoading}
                        canSearch={true}
                        searchValueChanged={searchValueChanged}
                        manualSorting={true}
                        columnSortingChanged={columnSortingChanged}
                    />

                    <ConfirmationDialog
                        showDialog={showConfirmationDialog}
                        handleAccept={handleDelete}
                        handleCancel={handleCloseConfirmation}
                        heading={"Confirm Delete"}
                        message={confirmDeleteMessage}
                    />
                    {showStatusTracker && <ScorecardVerificationProcessTrackerEdit id={selectedId} scorecardId={selectedScorecardId} showModal={showStatusTracker} closeModal={handleCloseStatusTracker} />}
                    {showEmailProgress && <ScorecardVerificationModal verificationProcessId={verificationProcessId} showEmailProgressStatusModal={showEmailProgress} closeShowEmailProgressStatusModal={handleCloseEmailProgress} scorecardId={props.scorecardId} />}
                    {showAllEmailProgress && <ScorecardVerificationModal verificationProcessId={0} showEmailProgressStatusModal={showAllEmailProgress} closeShowEmailProgressStatusModal={handleCloseAllEmailProgress} scorecardId={props.scorecardId} />}

                </DialogContent>
                <DialogActions>
                    <MDButton variant="gradient" color="info" onClick={() => handleSendAllUsersEmail()}>Email all stages of Verification Process</MDButton>
                </DialogActions>
            </Dialog>

            {documentsPopUp && <SupportingDocuments display={documentsPopUp} handleCancel={() => onDocumentPopupClose()} link={link} id={selectedDocumentId.toString()} scorecardId={props.scorecardId} />}
        </>
    )
}